/**
 * Emits schema.org structured data describing an actual lottery draw (game,
 * date and the numbers that came out) so Google can understand the outcome of
 * each result page rather than just the page's topic. Rendered in the body —
 * valid placement for JSON-LD, and it lets us use data fetched by the page.
 */
export type DrawSchemaInput = {
  gameName: string;
  gameSlug: string;
  drawDate: string; // YYYY-MM-DD
  winningNumbers: number[];
  bonusNumbers?: number[];
  multiplier?: number | null;
  stateName?: string | null;
  url: string; // absolute canonical URL of the page
};

const ORIGIN = "https://usalotteryclub.com";

export function drawSchema(d: DrawSchemaInput): Record<string, unknown> {
  const numbers = [...(d.winningNumbers ?? [])].join(", ");
  const bonus = (d.bonusNumbers ?? []).join(", ");
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `${d.gameName} winning numbers — ${d.drawDate}`,
    description:
      `Official ${d.gameName} draw held on ${d.drawDate}. Winning numbers: ${numbers}` +
      (bonus ? `; bonus: ${bonus}` : "") +
      (d.multiplier ? `; multiplier: ${d.multiplier}x` : "") +
      ".",
    url: d.url,
    identifier: `${d.gameSlug}-${d.drawDate}`,
    temporalCoverage: d.drawDate,
    datePublished: d.drawDate,
    dateModified: d.drawDate,
    isAccessibleForFree: true,
    license: `${ORIGIN}/`,
    keywords: [d.gameName, "lottery results", "winning numbers", d.drawDate].concat(
      d.stateName ? [d.stateName] : [],
    ),
    spatialCoverage: d.stateName
      ? { "@type": "Place", name: d.stateName }
      : { "@type": "Country", name: "United States" },
    creator: { "@type": "Organization", name: "USA Lottery Club", url: ORIGIN },
    variableMeasured: [
      { "@type": "PropertyValue", name: "Winning numbers", value: numbers },
      ...(bonus ? [{ "@type": "PropertyValue", name: "Bonus numbers", value: bonus }] : []),
      ...(d.multiplier
        ? [{ "@type": "PropertyValue", name: "Multiplier", value: `${d.multiplier}x` }]
        : []),
    ],
    about: {
      "@type": "Game",
      name: d.gameName,
      url: `${ORIGIN}/games/${d.gameSlug}`,
    },
  };
}

export function DrawJsonLd({ draws }: { draws: DrawSchemaInput[] }) {
  const valid = draws.filter((d) => d.gameName && d.drawDate && (d.winningNumbers?.length ?? 0) > 0);
  if (!valid.length) return null;
  const payload = valid.length === 1 ? drawSchema(valid[0]) : valid.map(drawSchema);
  return (
    <script
      type="application/ld+json"
      // JSON.stringify output is escaped below to keep it safe inside <script>.
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(payload).replace(/</g, "\\u003c"),
      }}
    />
  );
}
